import {
  ECaseMessagesKeys,
  EClaimMessagesKeys,
  EInsurerMessagesKeys,
  EStepsName,
  ESummaryMessagesKeys,
  ETelematicsMessagesKeys,
  IOnboardingModel,
  IStepRenderModel,
} from "../types";
import { get } from "draivn-ui-domain";
import { getAsyncComponent } from "@/modules/app";
import {
  postInsurerStep,
  postClaimStep,
  postTelematicsStep,
  postOnboardingComplete,
} from "../services";
import { TELEMATICS_EMPTY_MODEL } from "./telematics-empty-model";
import { CLAIM_EMPTY_MODEL } from "./claim-empty-model";

const getData = (
  key: EStepsName,
  data: IOnboardingModel,
  defaultValue: unknown = null
) => {
  const value = get(data, `sections[${key}].data`) || defaultValue;
  return value;
};

const isAttorneyInitiated = (model: IOnboardingModel): boolean => {
  const access = model.access;

  if (access && access.length > 0) {
    return access[0].role === "LAW_FIRM";
  }

  return false;
};

const getClaimCaseKeys = (model: IOnboardingModel) => {
  if (isAttorneyInitiated(model)) {
    return {
      TITLE: ECaseMessagesKeys.TITLE,
      TABNAME: ECaseMessagesKeys.TABNAME,
      SUMMARY_TABNAME: ECaseMessagesKeys.SUMMARY_TABNAME,
      SUMMARY_TITLE: ECaseMessagesKeys.SUMMARY_TITLE,
    };
  }

  return {
    TITLE: EClaimMessagesKeys.TITLE,
    TABNAME: EClaimMessagesKeys.TABNAME,
    SUMMARY_TABNAME: EClaimMessagesKeys.SUMMARY_TABNAME,
    SUMMARY_TITLE: EClaimMessagesKeys.SUMMARY_TITLE,
  };
};

// TODO: refactor  - fields title and pageTitle to main config
export const getSteps = (model: IOnboardingModel) => {
  const steps = new Map<EStepsName, IStepRenderModel>();

  // Standard fleet onboarding: uses insurer step
  if (model.all_sections.includes(EStepsName.INSURER)) {
    steps.set(EStepsName.INSURER, {
      type: EStepsName.INSURER,
      pageTitle: EInsurerMessagesKeys.TITLE,
      title: EInsurerMessagesKeys.TABNAME,
      component: getAsyncComponent(
        () => import("../components/steps/CompanyStep.vue")
      ),
      data: getData(EStepsName.INSURER, model),
      save: postInsurerStep,
      valid: model.sections[EStepsName.INSURER]?.valid || false,
    });
  }

  // Claim onboarding (defendant/claimant): uses claim step
  // Attorney flow uses "Case" terminology, TPA uses "Claim"
  if (model.all_sections.includes(EStepsName.CLAIM)) {
    const claimCaseKeys = getClaimCaseKeys(model);

    steps.set(EStepsName.CLAIM, {
      type: EStepsName.CLAIM,
      pageTitle: claimCaseKeys.TITLE,
      title: claimCaseKeys.TABNAME,
      component: getAsyncComponent(
        () => import("../components/steps/CompanyStep.vue")
      ),
      data: getData(EStepsName.CLAIM, model, CLAIM_EMPTY_MODEL),
      save: postClaimStep,
      valid: model.sections[EStepsName.CLAIM]?.valid || false,
    });
  }

  if (model.all_sections.includes(EStepsName.TELEMATICS)) {
    steps.set(EStepsName.TELEMATICS, {
      type: EStepsName.TELEMATICS,
      pageTitle: ETelematicsMessagesKeys.TITLE,
      title: ETelematicsMessagesKeys.TABNAME,
      component: getAsyncComponent(
        () => import("../components/steps/TelematicsStep.vue")
      ),
      data: getData(EStepsName.TELEMATICS, model, [
        structuredClone(TELEMATICS_EMPTY_MODEL),
      ]),
      save: postTelematicsStep,
      valid: model.sections[EStepsName.TELEMATICS]?.valid || false,
    });
  }
  if (model.all_sections.includes(EStepsName.SUMMARY)) {
    const isClaimFlow = model.all_sections.includes(EStepsName.CLAIM);

    let summaryTabName: string = ESummaryMessagesKeys.TABNAME;
    let summaryPageTitle: string = ESummaryMessagesKeys.TITLE;

    if (isClaimFlow) {
      const claimCaseKeys = getClaimCaseKeys(model);

      summaryTabName = claimCaseKeys.SUMMARY_TABNAME;
      summaryPageTitle = claimCaseKeys.SUMMARY_TITLE;
    }

    steps.set(EStepsName.SUMMARY, {
      type: EStepsName.SUMMARY,
      pageTitle: summaryPageTitle,
      title: summaryTabName,
      component: getAsyncComponent(
        () => import("../components/steps/SummaryStep.vue")
      ),
      data: getData(EStepsName.SUMMARY, model),
      save: postOnboardingComplete,
      valid: model.sections[EStepsName.SUMMARY]?.valid || false,
    });
  }
  return steps;
};
